export interface PageHeroProps {
  eyebrow?: string;
  title: string;
  titleAccent?: string;
  subtitle: string;
  backgroundImage?: string;
  primaryCTA?: { label: string; href: string; internal?: boolean };
  secondaryCTA?: { label: string; href: string };
  breadcrumb?: string;
  fullScreen?: boolean;
  showPhone?: boolean;
  stats?: { value: string; label: string }[];
}

const FALLBACK_IMG =
  "https://public.readdy.ai/ai/img_res/c5478282f3f509b8f54171b6c4a7f21d.jpg";

export default function PageHero({
  eyebrow,
  title,
  titleAccent,
  subtitle,
  backgroundImage,
  primaryCTA,
  secondaryCTA,
  breadcrumb,
  fullScreen = true,
  showPhone = true,
  stats,
}: PageHeroProps) {
  const heightClass = fullScreen ? "min-h-screen" : "min-h-[82vh]";
  const imgSrc = backgroundImage || FALLBACK_IMG;

  return (
    <header
      className={`relative ${heightClass} flex items-end overflow-hidden bg-[#0d0d0d]`}
      role="banner"
    >
      {/* Background image — identical pattern to BodyTypeGrid cards */}
      <img
        src={imgSrc}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />

      {/* Gradient overlay — exact same class pattern as BodyTypeGrid which is confirmed readable */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/20" />

      {/* Gold top bar */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px] z-10"
        style={{
          background:
            "linear-gradient(to right,transparent,#C9A96E 30%,#E5C99A 50%,#C9A96E 70%,transparent)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-40 pb-20 lg:pb-28">

        {breadcrumb && (
          <p className="text-white text-xs tracking-widest uppercase mb-5 font-sans">
            {breadcrumb}
          </p>
        )}

        {eyebrow && !breadcrumb && (
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-12 inline-block bg-gold-DEFAULT" />
            <span className="text-gold-DEFAULT text-xs font-semibold tracking-[0.3em] uppercase font-sans">
              {eyebrow}
            </span>
            <span className="h-px w-12 inline-block bg-gold-DEFAULT" />
          </div>
        )}

        <h1
          className="font-serif text-white leading-none mb-6 max-w-4xl"
          style={{ fontSize: "clamp(2.8rem,6.5vw,6rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
        >
          {title}
          {titleAccent && (
            <>
              <br />
              <span className="text-gold-light" style={{ fontWeight: 700 }}>{titleAccent}</span>
            </>
          )}
        </h1>

        <div className="flex items-center gap-4 mb-8">
          <span className="h-[3px] w-24 inline-block bg-gold-DEFAULT" />
          <span className="h-px w-16 inline-block bg-white/30" />
        </div>

        <p className="text-white font-sans font-normal max-w-2xl text-lg leading-relaxed mb-10">
          {subtitle}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mb-10">
          {primaryCTA && (
            <a
              href={primaryCTA.href}
              rel={primaryCTA.internal ? undefined : "noopener noreferrer"}
              className="inline-flex items-center justify-center gap-3 bg-gold-DEFAULT text-charcoal font-bold text-xs px-10 py-5 whitespace-nowrap cursor-pointer tracking-widest uppercase hover:bg-gold-light transition-all"
            >
              {primaryCTA.label}
              <i className="ri-arrow-right-line" />
            </a>
          )}
          {secondaryCTA && (
            <a
              href={secondaryCTA.href}
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 text-white font-semibold text-xs px-10 py-5 whitespace-nowrap cursor-pointer tracking-widest uppercase hover:bg-white/10 transition-all border border-white/50"
            >
              {secondaryCTA.label}
            </a>
          )}
        </div>

        {showPhone && (
          <a
            href="tel:9527693208"
            className="inline-flex items-center gap-3 cursor-pointer whitespace-nowrap hover:opacity-80 transition-opacity"
            aria-label="Call United Auto Group"
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border border-gold-DEFAULT bg-gold-DEFAULT/10">
              <i className="ri-phone-fill text-gold-DEFAULT" />
            </div>
            <span className="text-white font-sans font-medium text-base tracking-wide">
              (952) 769-3208 — Call Anytime
            </span>
          </a>
        )}

        {stats && stats.length > 0 && (
          <div className="flex flex-wrap gap-10 md:gap-16 mt-16 pt-10 border-t border-white/20">
            {stats.map((stat) => (
              <div key={stat.label}>
                <div
                  className="font-serif text-gold-light leading-none mb-1"
                  style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)" }}
                >
                  {stat.value}
                </div>
                <div className="text-white font-sans text-xs tracking-widest uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {fullScreen && (
        <div className="absolute bottom-8 right-10 hidden md:flex flex-col items-center gap-2 z-10">
          <span
            className="text-white/50 font-sans text-[10px] tracking-[0.35em] uppercase"
            style={{ writingMode: "vertical-rl" }}
          >
            Scroll
          </span>
          <div className="w-px h-12 bg-gradient-to-b from-gold-DEFAULT to-transparent" />
        </div>
      )}
    </header>
  );
}
